import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { extractDomain } from '../services/websiteAnalyzer';

const ManualWebsiteEntry = ({ onWebsitesAdd, disabled }) => {
  const [manualUrls, setManualUrls] = useState('');
  const [manualCompany, setManualCompany] = useState('');
  const [bulkEntry, setBulkEntry] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!manualUrls.trim()) {
      alert('Please enter at least one website URL.');
      return;
    }

    // For single entry mode
    if (!bulkEntry) {
      if (!manualUrls.trim()) {
        alert('Please enter a website URL.');
        return;
      }
      
      const url = manualUrls.trim();
      const company = manualCompany.trim() || extractDomain(url);
      
      const newWebsite = {
        url: url.startsWith('http') ? url : `https://${url}`,
        company: company
      };
      
      onWebsitesAdd([newWebsite]);
      setManualUrls('');
      setManualCompany('');
      return;
    }
    
    // For bulk entry mode - parse URLs
    const urlEntries = manualUrls
      .split(/[\n,;]+/)  // Split by newline, comma, or semicolon
      .filter(entry => entry.trim())
      .map(entry => {
        // Check if entry contains both URL and company (separated by a pipe or tab)
        const parts = entry.split(/[|\t]+/).map(part => part.trim());
        
        const url = parts[0];
        // Use provided company name or extract from domain if not provided
        const company = parts.length > 1 ? parts[1] : extractDomain(url);
        
        return {
          url: url.startsWith('http') ? url : `https://${url}`,
          company: company
        };
      });
    
    if (urlEntries.length === 0) {
      alert('No valid URLs found.');
      return;
    }
    
    onWebsitesAdd(urlEntries);
    setManualUrls('');
    setManualCompany('');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="upload-card"
    >
      <div className="upload-header">
        <SafeIcon icon="FiEdit" />
        <h3 className="upload-title">Manual Entry</h3>
      </div>
      
      <div className="entry-mode-toggle">
        <button 
          className={`toggle-button ${!bulkEntry ? 'active' : ''}`}
          onClick={() => setBulkEntry(false)}
          type="button"
        >
          Single Entry
        </button>
        <button 
          className={`toggle-button ${bulkEntry ? 'active' : ''}`}
          onClick={() => setBulkEntry(true)}
          type="button"
        >
          Bulk Entry
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        {!bulkEntry ? (
          // Single entry mode
          <>
            <div className="form-group">
              <label className="form-label">Website URL</label>
              <input
                type="text"
                value={manualUrls}
                onChange={(e) => setManualUrls(e.target.value)}
                placeholder="Enter website URL (e.g., example.com)"
                className="form-input"
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                value={manualCompany}
                onChange={(e) => setManualCompany(e.target.value)}
                placeholder="Enter company name"
                className="form-input"
                disabled={disabled}
              />
            </div>
          </>
        ) : (
          // Bulk entry mode
          <div className="form-group">
            <label className="form-label">Website URLs & Company Names</label>
            <textarea
              value={manualUrls}
              onChange={(e) => setManualUrls(e.target.value)}
              placeholder="Enter one per line:
example.com | Example Company
another.com | Another Company
thirdsite.com"
              className="form-input"
              style={{ minHeight: '120px', resize: 'vertical' }}
              disabled={disabled}
            />
            <div className="input-help">
              Format: website.com | Company Name (one per line)
              <br />
              Company name is optional - will use domain if not provided
            </div>
          </div>
        )}
        
        <button
          type="submit"
          className="upload-button"
          disabled={disabled}
          style={{ opacity: disabled ? 0.6 : 1 }}
        >
          Add {bulkEntry ? 'Websites' : 'Website'}
        </button>
      </form>
      
      <style jsx="true">{`
        .entry-mode-toggle {
          display: flex;
          margin-bottom: 1rem;
          border: 1px solid #e5e7eb;
          border-radius: var(--small-radius);
          overflow: hidden;
        }
        
        .toggle-button {
          flex: 1;
          padding: 0.5rem;
          background: none;
          border: none;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .toggle-button.active {
          background-color: var(--primary-color);
          color: white;
          font-weight: 500;
        }
        
        .toggle-button:not(.active):hover {
          background-color: #f3f4f6;
        }
        
        .input-help {
          font-size: 0.75rem;
          color: var(--text-light);
          margin-top: 0.25rem;
        }
      `}</style>
    </motion.div>
  );
};

export default ManualWebsiteEntry;